import { createRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { rootRoute } from './__root'
import { WorkspaceHeader } from '@/components/WorkspaceHeader'
import { apiClient } from '@/lib/api'
import { usePlatform } from '@/hooks/usePlatform'
import { useOpenProject } from '@/providers/WerkshopProvider'
import { useSequencerStore } from '@/stores/sequencerStore'
import { ConfirmDialog } from '@/components/panels/ConfirmDialog'

/**
 * Project-scoped settings. Edits the currently-open project
 * (resolved through `useOpenProject`) so changes flow straight
 * into the same audio engine session.
 */
export const werkshopSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/werkshop/settings',
  beforeLoad: async () => {
    try {
      await apiClient.get('/api/v1/auth/me')
    } catch {
      throw redirect({ to: '/login' })
    }
  },
  component: WerkshopSettings,
})

function WerkshopSettings() {
  const { projectId, project, container } = useOpenProject()
  const navigate = useNavigate()
  const platform = usePlatform()
  const setBpm = useSequencerStore(s => s.setBpm)
  const renameProject = useSequencerStore(s => s.renameProject)
  const deleteContainer = useSequencerStore(s => s.deleteContainer)
  const setConformToProjectBpm = useSequencerStore(
    s => s.setConformToProjectBpm,
  )
  const [confirmDelete, setConfirmDelete] = useState(false)

  if (!project || !projectId) {
    return (
      <div className="min-h-[100dvh] bg-surface-base">
        <WorkspaceHeader
          title="Project settings"
          workspaceSwitcher="werkshop"
        />
        <main
          className={
            platform.isNative
              ? 'w-full px-3 sm:px-4 py-4'
              : 'max-w-3xl mx-auto px-4 sm:px-6 py-8'
          }
        >
          <p className="text-sm text-content-secondary">
            No project open. Return to{' '}
            <a
              href="/werkshop/projects"
              className="text-accent hover:underline"
            >
              Sequencer
            </a>{' '}
            and select or create a project.
          </p>
        </main>
      </div>
    )
  }

  const handleDelete = () => {
    deleteContainer(projectId)
    setConfirmDelete(false)
    navigate({ to: '/werkshop/projects' })
  }

  return (
    <div className="min-h-[100dvh] bg-surface-base">
      <WorkspaceHeader
        title={`${project.name} · Settings`}
        workspaceSwitcher="werkshop"
      />
      <main
        className={
          platform.isNative
            ? 'w-full px-3 sm:px-4 py-4 flex flex-col gap-6'
            : 'max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8 flex flex-col gap-8'
        }
      >
        <Section title="Project metadata">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-content-tertiary text-xs uppercase tracking-wider">
              Name
            </span>
            <input
              type="text"
              value={project.name}
              onChange={e => renameProject(project.id, e.target.value)}
              className="bg-surface-sunken border border-brd-subtle rounded px-3 py-2 text-content-primary focus:outline-none focus:ring-2 focus:ring-[color:var(--accent-halo)]"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm mt-4">
            <span className="text-content-tertiary text-xs uppercase tracking-wider">
              BPM
            </span>
            <input
              type="number"
              min={30}
              max={300}
              step={0.1}
              value={project.bpm}
              onChange={e =>
                setBpm(
                  Math.max(30, Math.min(300, Number(e.target.value) || 120)),
                )
              }
              className="bg-surface-sunken border border-brd-subtle rounded px-3 py-2 text-content-primary w-32 focus:outline-none focus:ring-2 focus:ring-[color:var(--accent-halo)]"
            />
          </label>
        </Section>

        {container && (
          <Section title="Sample conform">
            <label className="flex items-center gap-3 text-sm">
              <input
                type="checkbox"
                checked={container.conformToProjectBpm === true}
                onChange={e => setConformToProjectBpm(e.target.checked)}
                className="h-4 w-4 rounded border-brd-subtle bg-surface-sunken accent-[color:var(--accent)]"
              />
              <span className="text-content-primary">
                Conform samples to project BPM
              </span>
            </label>
            <p className="text-xs text-content-tertiary mt-2">
              When on, sample pads with a known source BPM route through the
              Rubber Band worklet so chopped loops stretch to project tempo
              without pitch drift.
            </p>
          </Section>
        )}

        <Section title="Danger zone" tone="danger">
          <p className="text-sm text-content-secondary mb-3">
            Delete this project. All banks, patterns, and pad assignments are
            removed locally; cloud-synced projects are removed on the server
            too.
          </p>
          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            className="btn-secondary btn-sm border-destructive text-destructive hover:bg-destructive/10"
          >
            Delete project
          </button>
        </Section>
      </main>
      <ConfirmDialog
        isOpen={confirmDelete}
        title={`Delete "${project.name}"?`}
        description="All banks, patterns, and pad assignments are removed. This cannot be undone."
        confirmLabel="Delete"
        destructive
        onConfirm={handleDelete}
        onClose={() => setConfirmDelete(false)}
      />
    </div>
  )
}

function Section({
  title,
  tone = 'neutral',
  children,
}: {
  title: string
  tone?: 'neutral' | 'danger'
  children: React.ReactNode
}) {
  const toneClass =
    tone === 'danger' ? 'border-destructive/40' : 'border-brd-subtle'
  return (
    <section className={`rounded-xl border ${toneClass} bg-surface-raised p-5`}>
      <h2 className="text-sm font-semibold text-content-primary mb-3">
        {title}
      </h2>
      {children}
    </section>
  )
}
